<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref } from 'vue';
import { useDealershipStore } from '@/stores/dealership';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const props = defineProps({
    dealership: {
        type: Object,
        default: () => ({
            name: '',
            plan_slug: null,
            is_white_label: false,
            is_active: true,
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal', 'cancel-modal']);

const form = ref({
    name: props.dealership.name,
    plan: props.dealership.plan_slug,
    is_white_label: props.dealership.is_white_label,
    is_active: props.dealership.is_active,
});

const { planOptions } = storeToRefs(useOptionsStore());
const { storeDealership, updateDealership } = useDealershipStore();

const save = async () => {
    try {
        if (props.mode === 'Create') {
            await storeDealership({
                name: form.value.name,
                plan: form.value.plan,
                is_white_label: form.value.is_white_label,
            });
        } else {
            await updateDealership({
                hash: props.dealership.hash,
                name: form.value.name,
                plan: form.value.plan,
                is_active: form.value.is_active,
                is_white_label: form.value.is_white_label,
            });
        }

        emit('close-modal');
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
};
</script>
<template>
<ModalWrapper @cancel-modal="emit('cancel-modal')">
    <template #header>{{ mode }} {{ $t('label.dealership') }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { what: $t('label.dealership') }) }}</template>

    <form @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledInput
                v-model="form.name"
                name="name"
                :placeholder="$t('label.name')"
                :label="$t('label.name')" />
            <StyledDropdown
                v-model="form.plan"
                :options="planOptions"
                name="plan"
                :label="$t('label.plan')"
                :placeholder="$t('label.plan')" />
        </div>

        <div class="mt-2 space-y-3">
            <div
                v-if="mode === 'Edit'"
                class="flex items-center gap-2 text-xs text-ink-light">
                <span class="font-semibold uppercase tracking-wider">{{ $t('label.slug') }}</span>
                <span class="font-mono">{{ dealership.slug }}</span>
            </div>
            <div>
                <StyledRoundSwitch
                    v-model="form.is_white_label"
                    :label="$t('label.white_label')"
                    inset
                    name="is_white_label" />
                <p class="text-xs text-ink-light mt-1">{{ $t('label.white_label_hint') }}</p>
            </div>
            <StyledRoundSwitch
                v-if="mode === 'Edit'"
                v-model="form.is_active"
                :label="$t('label.active')"
                inset
                name="is_active" />
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
